import { emitter, init, launchChat } from "@liquidweb-dev/lw-chat-js";
import { onMounted, ref } from "vue";
import chatConfig from "@/utils/lw-chat.config";
import ChatError from "@/components/smart/modals/ChatError";
import store from "@/store";

const IDLE_TIME_LIMIT = 30;

export const useChat = () => {
  const chatInviteVisible = ref(false);
  const invitedToChat = ref(false);
  const showChatInvite = (value) => {
    if (!value) {
      invitedToChat.value = true;
    }
    chatInviteVisible.value = value;
  };

  const startIdleChatInvite = () => {
    const idleCounter = ref(0);
    const idleCheck = setInterval(() => {
      if (idleCounter.value >= IDLE_TIME_LIMIT) {
        if (window.outerWidth > 768) {
          chatInviteVisible.value = true;

          store.dispatch("analytics/send", {
            name: "structured",
            data: {
              category: "chat",
              action: "checkoutChatInvitation_displayed",
            },
          });
        }
        clearInterval(idleCheck);
      } else {
        idleCounter.value += 1;
      }
    }, 1000);

    document.addEventListener("click", () => (idleCounter.value = 0));
    document.addEventListener("blur", () => (idleCounter.value = 0));
    document.addEventListener("keypress", () => (idleCounter.value = 0));
  };

  return { chatInviteVisible, invitedToChat, showChatInvite, startIdleChatInvite };
};

/***************************************************************************************************
 Set default token to an empty string to pass into start chat when the user isn't logged. This is
 done in order to support the ability to preselect a department, as it requires a token to be set to
 do so.
 **************************************************************************************************/
const start = (extraDetails, token = "") => {
  const ChatAuthToken = splitAuthToken(token);
  return init(chatConfig).then(() =>
    launchChat({ hiddenPrechatFields: { ...ChatAuthToken, ...extraDetails } })
  );
};

const DUMMY_TOKEN =
  "eyJ0eXAiOiJKV1QiLCJhbGciOi1234567899.eyJ2ZXIiOiIxIiwiaWF0IjoxNjEwNTU1MDAyLCJzdWIiOiJ7XCJleHRyYW5ldFwiOntcImNsaWVudFwiOjM4NTgyLFwidXNlclwiOjYxOTAzfX0iLCJleHAiOjE2MTA1NjIyMDIsImFsdiI6Mn0.DXbYlgZngo2usCax_v5wdEXyLS--HJu-Q9exBXQePPA";

const openChatDummy = async (extraDetails) => {
  try {
    await start(extraDetails, DUMMY_TOKEN);
  } catch {
    store.dispatch("modals/open", { template: ChatError });
    return Promise.reject();
  }
  return Promise.resolve();
};

export const openChat = async (extraDetails) => {
  try {
    const { token } = await store.dispatch("auth/getChatToken");
    try {
      await start(extraDetails, token);
    } catch {
      store.dispatch("modals/open", { template: ChatError });
      return Promise.reject();
    }
  } catch {
    return openChatDummy(extraDetails);
  }
  return Promise.resolve();
};

export const chatBtnMixin = {
  props: {
    department: String,
  },
};

export const chatBtnSetup = (props) => {
  const { startIdleChatInvite } = useChat();
  const loading = ref(false);
  const chatting = ref(false);
  const chat = () => {
    loading.value = true;
    const preselectDepartment = store.getters["surveys/requireSurvey"]
      ? store.getters["surveys/getSurveyType"]
      : props.department;

    openChat({ preselectDepartment })
      .then(() => {
        chatting.value = true;
        emitter.on("afterDestroy", () => {
          chatting.value = false;
        });
      })
      .finally(() => {
        loading.value = false;
      });
  };

  onMounted(() => {
    startIdleChatInvite();
  });

  return {
    loading,
    chatting,
    chat,
  };
};

/**
 * Splits a JWT token into three chunks of maximum 255 characters each to comply with hidden prechat fields limitations.
 *
 * @param token - The JWT token to split
 * @returns An object containing the token split into three parts, or null if the input is invalid
 */
export function splitAuthToken(token) {
  const CHUNK_SIZE = 255;
  const MAX_TOTAL_LENGTH = CHUNK_SIZE * 3;

  // Return null for invalid inputs
  if (!token || typeof token !== "string" || token.trim().length === 0) {
    return null;
  }

  // Return null if token is too long
  if (token.length > MAX_TOTAL_LENGTH) {
    console.error(
      `Token length (${token.length}) exceeds maximum allowed length of ${MAX_TOTAL_LENGTH} characters`
    );
    return null;
  }

  // Create the chunks
  const chunks = {
    JWT1: token.slice(0, CHUNK_SIZE),
    JWT2: token.slice(CHUNK_SIZE, CHUNK_SIZE * 2),
    JWT3: token.slice(CHUNK_SIZE * 2),
  };

  // Validate reconstruction
  const isValid = Object.values(chunks).join("") === token;

  if (!isValid) {
    console.error(
      "Token splitting validation failed - reconstructed token does not match original"
    );
    return null;
  }

  return chunks;
}
